// Wire-format translation for the contractor compliance portal — RON-5465.
//
// The API speaks snake_case with nested steps; every screen here reads a flat camelCase shape.
// This is the only file that knows both, and it runs on BOTH paths — the fixtures are in wire
// format precisely so the mock exercises it too.
//
// Nothing here invents a value. Where the server says nothing, the mapper says nothing: a portal
// that guesses at whether a document was approved is worse than one that says it does not know.

// ── A document ───────────────────────────────────────────────────────────────────
//
// One shape for both kinds. They differ in which half is filled: a `bank_sign` carries the
// signature fields, an `upload_request` carries the file ones. Binding one shape means the list
// renders from a single component and the step counts come from the same place.
export const documentFromApi = (api) => {
  if (!api) return null;

  return {
    id: api.id,
    kind: api.kind,
    title: api.title,
    description: api.description ?? null,
    required: api.required === true,
    state: api.state,
    // The server's own answer to "is this still in the way". §3 computes it per state and the
    // submit step counts the same field, so re-deriving it here would let the two disagree.
    outstanding: api.outstanding === true,

    // Read-and-sign
    version: api.version ?? null,
    ackMethod: api.ack_method ?? null,
    readToEnd: api.read_to_end === true,
    signedAt: api.signed_at ?? null,
    signedName: api.signed_name ?? null,

    // ── Replacing a document — Phase C (21-October-2026) ─────────────────────────────────────
    //
    // "Any state can be replaced — not uploaded, rejected, pending, approved, expired — before
    // or after submit, until the link closes."
    //
    // `uploadAction` is the SERVER'S answer to "what can be done with this document", and it is
    // what the button says. We do not work it out from `state`: that is the mistake Phase 3
    // just undid for the clearance banner, and the rules here are subtler — a `pending`
    // document is replaceable but is NOT outstanding, and an `approved` one is replaceable but
    // costs the contractor their clearance.
    //
    // `null` means nothing can be done: the link has closed, or this is a renewal pack and the
    // document is not the one expiring.
    uploadAction: api.upload_action ?? null,
    canUpload: api.can_upload === true,

    // `withdraws_approval` — the AC-P-62 warning, shown BEFORE the upload area opens.
    // `renewal_possible` arrives with Phase D and is handled there.
    replaceWarning: api.replace_warning ?? null,

    // Phase B. A re-upload makes a NEW document row with a new id, and the old one becomes
    // history — so a card can say which file it replaced and when.
    versionNo: api.version_no ?? null,
    replacesUploadedAt: api.replaces_uploaded_at ?? null,

    // ── Renewals — Phases D and E ───────────────────────────────────────────────────────────
    //
    // A renewal is a replacement of an APPROVED document whose new expiry is LATER. It does not
    // withdraw clearance: the current version stays valid until the new one is approved, which
    // is the whole difference from an ordinary replacement.
    //
    // `renewalValidUntil` is the date in AC-P-80's first sentence — "stays valid until …".
    // Paired with `replace_warning: "renewal_possible"`, and meaningless without it.
    renewalValidUntil: api.renewal_valid_until ?? null,

    // The renewal in flight, once one is uploaded (AC-P-76, AC-P-78). `null` when there is
    // none — which is every document most of the time.
    renewal: api.renewal
      ? {
          // `awaiting_approval` — with the reviewer, nothing for the contractor to do.
          // `needs_action` — it was rejected and they have to try again.
          state: api.renewal.state ?? null,
          stillValidUntil: api.renewal.still_valid_until ?? null,
        }
      : null,

    // Renewal packs only (AC-P-77). True on the document that is expiring — the one the pack was
    // sent for. Every other document is shown but not offered (`can_upload: false`).
    needsRenewal: api.needs_renewal === true,

    // Upload
    // ── The reissue case ─────────────────────────────────────────────────────────────────────
    //
    // ADDED 16-October-2026, and it should have been here from the start. `PortalReadSign` has
    // read `doc.reissued` and `doc.updatedAt` since the component was written, the fixture has
    // set `reissued: true` on the third bank document, and this mapper dropped both in between.
    //
    // So the "Re-signature required — document was updated" badge HAS NEVER RENDERED. Not on
    // staging, not on the fixtures. We argued the case for the flag with the Laravel team over
    // two rounds while our own screen could not have shown it.
    //
    // `signed_version` is theirs rather than ours — offered unprompted: the highest version this
    // contractor signed in an EARLIER pack, and `null` unless it is lower than this pack's. It
    // is what lets the screen say which version they signed rather than only that it was older.
    reissued: api.reissued === true,
    signedVersion: api.signed_version ?? null,
    updatedAt: api.updated_at ?? null,

    acceptedFileTypes: api.accepted_file_types ?? null,
    expiryRequired: api.expiry_required === true,
    uploadedAt: api.uploaded_at ?? null,
    expiry: api.expiry ?? null,
    // §3: `file` is `held` or `none` — a FLAG, not a filename. We used to pass it through as
    // though it were one, which would have put the word "held" wherever a filename was shown.
    // The server never returns the name; it derives the bucket key itself.
    hasFile: api.file === "held",
    scan: api.scan ?? null,
    rejectedReason: api.rejected_reason ?? null,
    approvedAt: api.approved_at ?? null,
  };
};

const stepFromApi = (step = {}) => ({
  total: step.total ?? 0,
  outstanding: step.outstanding ?? 0,
  items: (step.items || []).map(documentFromApi).filter(Boolean),
});

// ── The pack (§3) ────────────────────────────────────────────────────────────────
//
// `GET /visitor/portal/pack` → the whole screen in one call, and the shape EVERY write returns
// too. §4: "every write returns the whole pack, not the document that changed. It costs one extra
// query and removes a class of bug where the screen's copy of step 4 drifts from the server's."
// So there is one mapper and callers replace their state wholesale rather than patching a row.
export const packFromApi = (records = {}) => {
  const steps = records.steps || {};

  return {
    contractor: {
      name: records.contractor?.name ?? null,
      company: records.contractor?.company ?? null,
      trade: records.contractor?.trade ?? null,
      email: records.contractor?.email ?? null,
    },
    // TOP-LEVEL, not inside `visit`: "We always know the customer, even when there is no
    // visit. So the banner heading can always draw it."
    customer: records.customer ?? null,
    visit: visitFromApi(records.visit),

    status: records.status ?? null,
    submitted: records.submitted === true,
    submittedAt: records.submitted_at ?? null,
    // Phase C. Documents stay replaceable while the link is live, even on a submitted pack —
    // which is the half of `read_only` that stopped being true.
    canReplaceDocuments: records.can_replace_documents === true,
    mode: modeFromApi(records.mode),
    // §5: read-only after submit is a product decision, not a side effect. Carried explicitly so
    // every control on the screen can ask one question rather than inferring from `submitted`.
    readOnly: records.read_only === true,

    // Named for what they are to the person reading, not for the table they came from:
    // `bank_documents` are the ones the customer wrote and the contractor reads.
    // §6.7, AC-P-67 — Phase D. The server computes this now; until 22-October we derived the
    // same four states from the rows, which was a second opinion waiting to disagree.
    //
    // `null` when absent rather than a default, so the banner draws nothing rather than
    // claiming a contractor is "not cleared yet" because a field was missing.
    siteAccess: siteAccessFromApi(records.site_access),

    readDocuments: stepFromApi(steps.bank_documents),
    myDocuments: stepFromApi(steps.my_documents),

    personnel: {
      required: steps.personnel?.required === true,
      confirmed: steps.personnel?.confirmed === true,
      // NOT SENT by the server — §8: step 3 is "a tick, not a crew list". Read anyway so the
      // chips draw on the fixtures for design review, and simply do not draw live.
      names: Array.isArray(steps.personnel?.names) ? steps.personnel.names : [],
    },

    submit: {
      canSubmit: steps.submit?.can_submit === true,
      // A RENEWAL PACK HAS NO SUBMIT AT ALL (AC-P-77). Each renewal goes straight to review, so
      // the step is not merely disabled — it is not a step.
      //
      // Defaults to TRUE when the server does not send it, which is every pack before Phase E:
      // the four-step pack is the norm and a missing field must not delete a step from it.
      required: steps.submit?.required !== false,
      // What is still in the way, assembled server-side. Each row carries the step it belongs to,
      // so the screen can send somebody back to the right place instead of to the top.
      outstanding: (steps.submit?.outstanding || []).map((item) => ({
        id: item.id ?? null,
        title: item.title,
        step: item.step ?? null,
        why: item.why ?? null,
      })),
    },
  };
};

// ── The visit this pack is for ───────────────────────────────────────────────────
// SHIPPED 09-October-2026 (answers-to-frontend-6). It rides on the pack, so there is no separate
// call and no separate fetch — `GetPortalVisit_api` existed only while this had no source.
//
// A PACK IS NOT A VISIT, and the wording on screen has to respect that. A contractor is vetted
// once; hosts book visits against them separately, so a contractor can have none, one, or many.
// What comes back is the NEAREST visit from today — the first by date that is `expected` or
// `checked_in` and has not ended, judged by the site's own calendar day — or `null`.
//
// Their note is explicit about the consequence: "Please word the banner so it does not promise
// more than that. 'Your next visit' is true; 'the visit this pack is for' is not always true."
//
// `access_expires` IS NOT SENT and will not be: "the portal has no closing date. A pack stays
// open until the admin decides it, so any date would be made up." The closing notice that used
// to read "This portal closes on …" has been removed rather than given an invented date.
// The §6.7 banner, as the server sends it.
//
// `paused: true` means a replaced document withdrew clearance — a state we had no way to derive
// and did not know existed. `renewal` sits on top of `cleared`: a renewal never turns the
// banner red, because the current version stays valid until the new one is approved.
// ── The pack's mode — Phase E ────────────────────────────────────────────────────────────────
//
// TWO KINDS, both vetting cycles: `preapproval` (no visit booked) and `renewal` (an expiring
// document). There is no "visit" mode — the 15-October revision removed it, and the prototype
// says so in its own comment: "vetting is decoupled from visits".
//
// `null` when the pack does not carry it, so the header falls back to what it drew before
// rather than asserting a kind it does not know.
export const modeFromApi = (api) => {
  if (!api) return null;

  return {
    kind: api.kind ?? null,
    requestedBy: api.requested_by
      ? { name: api.requested_by.name ?? null, email: api.requested_by.email ?? null }
      : null,
    // A renewal link is issued by the nightly sender rather than by a person. The header still
    // names the admin who sent the ORIGINAL pack — "on behalf of" — so there is somebody to ask.
    sentAutomatically: api.sent_automatically === true,
    // The `_local` twin, like every date in this product.
    completeBy: api.complete_by_local ?? api.complete_by ?? null,
    preapprovedSince: api.preapproved_since ?? null,
    // "RSP-PA-YYYY-MMDD-XX" on the all-done banner. A LABEL, not an id — it is the issue date
    // and the contractor's initials, and nothing looks it up.
    reference: api.reference ?? null,
  };
};

export const siteAccessFromApi = (api) => {
  if (!api) return null;

  return {
    state: api.state ?? null,
    needsAction: api.needs_action ?? 0,
    paused: api.paused === true,
    clearedUntil: api.cleared_until ?? null,
    renewal: api.renewal ?? null,
    renewalUntil: api.renewal_until ?? null,
  };
};

export const visitFromApi = (api) => {
  if (!api) return null;

  return {
    site: api.site ?? null,
    date: api.date ?? null,
    // Added by them, because a contractor visit can run several days.
    endDate: api.end_date ?? null,
    arrivalTime: api.arrival_time ?? null,
    host: api.host ?? null,
    purpose: api.purpose ?? null,
    // The visit's own `booking_code` — the same code on the invite email and at the kiosk, not a
    // portal-specific reference. "A second code would mean two numbers for one booking."
    reference: api.reference ?? null,
  };
};

// ── Sign-in (§1, §2) ─────────────────────────────────────────────────────────────
//
// `request-code` tells us NOTHING except that it was asked. §1: the reply is `{ "sent": true }`
// and it is identical for a real pack, an unknown guid and a finished one — "`sent: true` means
// 'we are not telling you', not 'the mail server accepted it'."
//
// CORRECTED 06-October-2026. This used to read a `masked_email` the server has never sent, and
// the verify screen used that value to decide whether to show the code box — so against the real
// API the box would never have appeared and nobody could have signed in.
//
// There is also no masked address to be had: the contractor's email first reaches us after
// sign-in. Showing one here would confirm the guid is real and reveal part of the address, which
// is the precise thing this endpoint is shaped to avoid — the guids travel in email.
export const requestCodeFromApi = (records = {}) => ({
  sent: records.sent === true,
});

// ── The link — Phase A (21-October-2026) ─────────────────────────────────────────────────────
//
// Arrives on BOTH `open` and `verify-code`, so it is mapped once.
//
// THIS IS WHERE THE CLOSING DATE LIVES. On 09-October the Laravel team told us the portal had
// none — "a pack stays open until the admin decides it, so any date would be made up" — and we
// deleted the banner, the header pill and the footer's expiry half. Phase A reinstates it, but
// as a property of the LINK rather than the pack: §3's own note says "the closing date is the
// link's, not the visit's: read `Records.link.expires_at_local`".
//
// So both facts were true in turn, and neither was a mistake. A pack has no closing date; the
// link that opens it does, 30 days by default (`VCM_PORTAL_LINK_DAYS`).
//
// `expiresAt` reads the `_local` twin and the contract is explicit: "Do not convert
// `expires_at` yourself." `timezone` comes with it so a screen can name the zone rather than
// implying the reader's own.
const linkFromApi = (api) => {
  if (!api) return null;

  return {
    // `preapproval` | `renewal`. The pack's `mode` object says far more (see Phase E); this is
    // what is knowable at sign-in, before there is a pack.
    kind: api.kind ?? null,
    expiresAt: api.expires_at_local ?? api.expires_at ?? null,
    timezone: api.timezone ?? null,
  };
};

// ── `POST /visitor/portal/open` — Phase A, the first call the page makes ─────────────────────
//
// Three outcomes, and only two of them are a 200:
//
//   · `valid`   → show the code screen. Carries `company` and the link.
//   · `expired` → show ONLY "this link has expired", when it closed, and who to contact.
//   · 404       → "This link is not valid.", and nothing else.
//
// `expired` covers three different things — the link lapsed, a NEWER LINK REPLACED IT, or the
// contractor was archived — and the server deliberately does not say which. A contractor who
// clicked an old email and one whose account was closed see the same screen, because telling
// them apart would leak the state of a pack to whoever holds a stale link.
//
// `contact` is `null` for a link the system sent itself (renewals). The screen must cope: "ask
// the person who sent this" is not sayable when nobody did.
export const openFromApi = (records = {}) => ({
  state: records.state ?? null,
  company: records.company ?? null,
  link: linkFromApi(records.link),

  // ── The verify screen's four, added 24-October-2026 (answers-to-frontend-21) ───────────────
  //
  // All four are `null` for good reasons the screen has to handle, not as an error case:
  // a customer with no logo, an address that cannot be used, a link with no sender on record,
  // and a link issued before Phase E. Every one of them falls back to wording that already
  // works, so none of them is allowed to blank a screen.

  // The customer's company-settings image — the same one their branded emails carry. `null`
  // when they have none, and then the company NAME stands in for it, permanently rather than
  // as a stopgap: a customer who never uploads one still has to be identified.
  logoUrl: records.logo_url ?? null,

  // Masked by the SERVER, to a fixed length that does not reveal the real one. We never build
  // one — no call before sign-in returns a real address, which is the whole point.
  maskedEmail: records.masked_email ?? null,

  // A label (issue date + the contractor's initials), not an id. For the footer.
  reference: records.reference ?? null,
  // Only on a closed link. The `_local` twin again.
  closedAt: records.closed_at_local ?? records.closed_at ?? null,
  timezone: records.timezone ?? null,
  // On BOTH states since 24-October. It used to be the expired screen's alone, which left the
  // one person who most needs a human — right address missing from a perfectly live link — as
  // the only one we could not point anywhere. Still `null` when there is no sender on record.
  contact: records.contact
    ? { name: records.contact.name ?? null, email: records.contact.email ?? null }
    : null,
});

// `verify-code` returns the session token. 60 minutes, pushed on by every call.
//
// `submitted` is carried because §2 says it is there for exactly one purpose: "so the screen knows
// immediately whether to draw the pack or the read-only 'already sent' view, without a second
// call."
export const verifyCodeFromApi = (records = {}) => ({
  token: records.token ?? records.session_token ?? null,
  expiresIn: records.expires_in ?? null,
  contractorName: records.contractor_name ?? null,
  company: records.company ?? null,
  submitted: records.submitted === true,
  // Added 21-October-2026. The header pill and the expiry notice read this; there is no second
  // call to fetch it, and the pack does not carry it.
  link: linkFromApi(records.link),
});

// The body of one readable document, for the scroll-to-end reader.
export const documentBodyFromApi = (records = {}) => ({
  id: records.id ?? null,
  title: records.title ?? null,
  // An array of paragraphs where the API sends one, a single block where it sends a string —
  // either way the reader renders paragraphs rather than one wall of text.
  body: Array.isArray(records.body)
    ? records.body
    : String(records.body ?? "")
        .split(/\n{2,}/)
        .map((part) => part.trim())
        .filter(Boolean),
});

// ── Writes ───────────────────────────────────────────────────────────────────────

// §5 names the three fields and when each is needed:
//
//   consent      ALWAYS — the tick
//   name         when `ack_method` is `esign`
//   read_to_end  when the document's `read_to_end` is true
//
// CORRECTED 06-October-2026. This used to send `ticked`, which is not a field the endpoint knows,
// and omitted `consent` and `read_to_end` entirely — so every live signature would have come back
// 409 "Please tick the box to confirm you agree." The screen was already collecting both facts;
// only this function was dropping them.
export const signToApi = ({ Name, Consent, ReadToEnd } = {}) => {
  const body = { consent: Consent === true };

  // A tick-only document still records a name — the contractor's own — so the row always says who.
  if (Name) body.name = Name;
  if (ReadToEnd !== undefined) body.read_to_end = ReadToEnd === true;

  return body;
};

// §6: `upload-url` is asked with the filename, because the extension is checked against the
// document's `accepted_file_types` before anything is signed.
export const uploadUrlToApi = ({ FileName } = {}) => ({ filename: FileName ?? "" });

// §4: "an upload is three calls, not one" — `upload-url`, then PUT to S3, then `uploaded`. This
// builds the body of the last one: the server checks the object is really there before it records
// anything, so the browser cannot claim a file it did not put.
//
// CORRECTED 06-October-2026. `filename` was missing and it is REQUIRED — §7, "the key is derived"
// from it, so the server uses it to look for exactly that object. Without it the third call could
// never find the file and every live upload would have ended 409.
export const uploadedToApi = ({ FileName, Expiry } = {}) => {
  const body = {};

  if (FileName) body.filename = FileName;
  if (Expiry) body.expiry = Expiry;

  return body;
};
